import React, {useCallback, useEffect, useRef, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Skeleton,
    Button,
    TablePagination,
    Slide,
    IconButton,
    Popover,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {InfoOutlined, DeleteOutline} from '@mui/icons-material';
import {TaxRepService, type TaxRepVO} from '@/services/manageService/taxRepService';
import type {ResponsePage} from '@/services/page.interface';
import {SnackbarField, InputField} from '@/components/common';
import TaxRepDetail from '@/pages/components/manage/other/tax-rep/TaxRepDetail';

function DeletePopover({nodeId, nodeName, onRemove}: {
    nodeId: number;
    nodeName: string;
    onRemove: (id: number) => void;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const handleClick = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);
    const handleConfirm = () => {
        onRemove(nodeId);
        handleClose();
    };
    return (
        <>
            <IconButton size="small" color="error" onClick={handleClick}>
                <DeleteOutline fontSize="small"/>
            </IconButton>
            <Popover open={!!anchorEl} anchorEl={anchorEl} onClose={handleClose}
                     anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}>
                <Box p={2}>
                    <Typography variant="body2" sx={{mb: 1}}>Confirm to delete "{nodeName}"?</Typography>
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={handleClose}>Cancel</Button>
                        <Button size="small" color="error" variant="contained" onClick={handleConfirm}>Confirm</Button>
                    </Stack>
                </Box>
            </Popover>
        </>
    );
}

const EMPTY_PAGE: ResponsePage<TaxRepVO> = {
    list: [],
    totalRow: 0,
    pageSize: 25,
    pageNumber: 1,
    lastPage: true,
    totalPage: 0,
    firstPage: true,
};

export default function TaxRepList() {
    const theme = useTheme();
    const [data, setData] = useState<ResponsePage<TaxRepVO>>(EMPTY_PAGE);
    const [loading, setLoading] = useState<boolean>(true);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false, message: '', severity: 'success',
    });
    const [taxRepName, setTaxRepName] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailId, setDetailId] = useState<number | undefined>(undefined);

    const initialLoadDone = useRef(false);

    const fetchData = useCallback(async (pageNum = 1, pageSize = 25) => {
        setLoading(true);
        try {
            const res = await TaxRepService.queryPage({
                pageNumber: pageNum,
                pageSize,
                pageType: 1,
                taxRepName: taxRepName || undefined,
            });
            setData(res ?? EMPTY_PAGE);
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Failed to load data';
            setSnackbar({open: true, message, severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, [taxRepName]);

    useEffect(() => {
        fetchData(page + 1, rowsPerPage);
        initialLoadDone.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage]);

    useEffect(() => {
        if (!initialLoadDone.current) return;
        const handler = setTimeout(() => {
            setPage(0);
            fetchData(1, rowsPerPage);
        }, 400);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taxRepName]);

    const handleRemove = async (id: number) => {
        setLoading(true);
        try {
            await TaxRepService.deleteById(id);
            setSnackbar({open: true, message: 'Deleted successfully!', severity: 'success'});
            fetchData(page + 1, rowsPerPage);
        } catch (e: unknown) {
            const message = e instanceof Error ? e.message : 'Delete failed!';
            setSnackbar({open: true, message, severity: 'error'});
        } finally {
            setLoading(false);
        }
    };

    const rows = data.list ?? [];

    return (
        <Slide direction="left" in={true} mountOnEnter unmountOnExit>
            <Box sx={{
                p: 2,
                bgcolor: 'transparent',
                height: 'calc(100vh - 120px)',
                minHeight: '70vh',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
                <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                       alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                    <Typography variant="h6" fontWeight={700} color="text.primary">Tax Representative</Typography>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}
                           alignItems={{xs: 'stretch', sm: 'center'}}>
                        <InputField size="small" label="Tax Representative" value={taxRepName}
                                    onChange={e => setTaxRepName(e.target.value)}/>
                        <Button variant="contained" color="secondary"
                                sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                onClick={() => {
                                    setDetailId(undefined);
                                    setDialogOpen(true);
                                }}>Create</Button>
                    </Stack>
                </Stack>

                <TableContainer
                    component={Paper}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                        boxShadow: theme.shadows[2],
                        bgcolor: 'background.paper',
                    }}
                >
                    <Table
                        size="small"
                        stickyHeader
                        sx={{
                            minWidth: 800,
                            '& .MuiTableCell-stickyHeader': {
                                bgcolor: 'background.paper',
                                zIndex: 3,
                                borderBottom: `1px solid ${theme.palette.divider}`,
                            },
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{fontWeight: 700}}>Tax Representative</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Telephone</TableCell>
                                <TableCell sx={{fontWeight: 700}}>Hereby authorize</TableCell>
                                <TableCell sx={{fontWeight: 700}}>BR Number</TableCell>
                                <TableCell sx={{fontWeight: 700, width: 120}}>Action</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading && [...Array(rowsPerPage).keys()].map(i => (
                                <TableRow key={i}>
                                    {[...Array(5).keys()].map(j => (
                                        <TableCell key={j}><Skeleton variant="text"/></TableCell>
                                    ))}
                                </TableRow>
                            ))}
                            {!loading && rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">No data</TableCell>
                                </TableRow>
                            )}
                            {!loading && rows.map((r, idx) => (
                                <TableRow hover key={r.id ?? `row-${idx}`}>
                                    <TableCell>{r.taxRepName || '-'}</TableCell>
                                    <TableCell>{r.telephone || '-'}</TableCell>
                                    <TableCell>{r.herebyAuthorize || '-'}</TableCell>
                                    <TableCell>{r.brNumber || '-'}</TableCell>
                                    <TableCell>
                                        <Stack direction="row" spacing={0.5}>
                                            <IconButton size="small" color="primary" onClick={() => {
                                                setDetailId(r.id);
                                                setDialogOpen(true);
                                            }}>
                                                <InfoOutlined fontSize="small"/>
                                            </IconButton>
                                            {r.id != null && (
                                                <DeletePopover nodeId={r.id}
                                                               nodeName={r.taxRepName || ''}
                                                               onRemove={handleRemove}/>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    component="div"
                    count={data.totalRow ?? 0}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={e => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    sx={{flexShrink: 0}}
                />

                <TaxRepDetail
                    open={dialogOpen}
                    taxRepId={detailId}
                    onClose={(p?: { refreshed?: boolean; message?: string; severity?: 'success' | 'error' }) => {
                        setDialogOpen(false);
                        setDetailId(undefined);
                        if (p?.message) setSnackbar({
                            open: true,
                            message: p.message,
                            severity: p.severity || 'success',
                        });
                        if (p?.refreshed) {
                            fetchData(page + 1, rowsPerPage);
                        }
                    }}
                />
                <SnackbarField
                    open={snackbar.open}
                    message={snackbar.message}
                    severity={snackbar.severity}
                    onClose={() => setSnackbar({...snackbar, open: false})}
                />
            </Box>
        </Slide>
    );
}
